import LegalLayout from './LegalLayout';

const P = ({ children }: { children: React.ReactNode }) => (
  <span className="legal-placeholder">{children}</span>
);

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="2026-06-18">
      <p>
        These Terms of Service ("Terms") form a binding agreement between you
        and <P>[YOUR LEGAL COMPANY NAME, s.r.o.]</P> ("RentNova", "we", "us")
        governing your use of the RentNova service available at rentnova.app
        ("Service"). By creating an account or using the Service, you agree to
        these Terms. If you do not agree, do not use the Service.
      </p>

      <h2>1. The Service</h2>
      <p>
        RentNova is a software-as-a-service (SaaS) tool for landlords and
        tenants to manage residential rentals: properties, tenant invitations,
        rent payments (via Stripe Connect), maintenance tickets, documents and
        e-signatures, and in-app communication.
      </p>

      <h2>2. Account</h2>
      <p>
        You must be at least 18 years old, have legal capacity to enter
        contracts, and provide accurate registration information. You are
        responsible for keeping your password confidential and for all activity
        under your account.
      </p>
      <p>
        You may not create accounts using false identities, register on behalf
        of someone who has not authorized you, or share your account with
        third parties.
      </p>

      <h2>3. Plans, fees and billing</h2>
      <ul>
        <li>
          We offer Free, Basic (€9/month), Pro (€19/month), and Business
          (€49/month) plans, with annual variants at a discount. Current plan
          details are shown on the <a href="/#pricing">pricing page</a>.
        </li>
        <li>
          Paid subscriptions are billed in advance through Stripe. The card on
          file is charged on each renewal. By subscribing you authorize these
          recurring charges.
        </li>
        <li>
          You can cancel at any time from the in-app Stripe Customer Portal.
          Cancellation takes effect at the end of the current billing period;
          we do not provide pro-rated refunds for partial periods, except where
          required by mandatory consumer law.
        </li>
        <li>
          We may change prices with 30 days' notice. Notice will be by email and
          in-app. Continued use after the effective date constitutes acceptance.
        </li>
        <li>
          All prices are in EUR and exclusive of VAT unless stated otherwise.
          VAT will be added based on your billing country.
        </li>
      </ul>

      <h2>4. Rent collection via Stripe Connect</h2>
      <p>
        Landlords on Pro and Business plans may collect rent from tenants
        through Stripe Connect. To do so, the landlord must complete Stripe's
        own onboarding (KYC, bank details). RentNova does not hold funds.
        Money flows from tenant → Stripe → landlord's bank, minus Stripe
        processing fees and any RentNova platform fee disclosed at onboarding.
      </p>
      <p>
        Each landlord using Stripe Connect is independently subject to
        <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noreferrer"> Stripe Connected Account Agreement</a>.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You agree NOT to use the Service to:</p>
      <ul>
        <li>Violate any applicable law, including housing, anti-discrimination, or data-protection laws.</li>
        <li>Upload content you do not own or have permission to share.</li>
        <li>Send spam, phishing, malware, or unsolicited commercial messages through the chat or email features.</li>
        <li>Attempt to gain unauthorized access to any account, server, or data not your own.</li>
        <li>Probe, scan, reverse-engineer, or interfere with the Service in any way.</li>
        <li>Misuse the e-signature feature to bind another person without their consent.</li>
        <li>Use automated scripts to scrape or extract data from the Service.</li>
      </ul>
      <p>
        We may suspend or terminate accounts that violate these rules, with or
        without notice, depending on severity.
      </p>

      <h2>6. Your content</h2>
      <p>
        You retain ownership of all data you upload (property info, tenant
        info, documents, chat messages, photos — "Your Content"). You grant us
        a non-exclusive, worldwide, royalty-free license to process Your Content
        solely as necessary to operate the Service.
      </p>
      <p>
        You represent and warrant that you have all rights and consents needed
        to upload Your Content — especially with respect to personal data about
        third parties (tenants). If you enter tenant data, you are the data
        controller for that data and you must have a lawful basis to do so.
      </p>

      <h2>7. Service availability</h2>
      <p>
        We aim for high availability but do not guarantee uptime. Scheduled
        maintenance, third-party outages (Supabase, Stripe, Vercel), or force
        majeure may interrupt the Service. We are not liable for losses arising
        from such interruptions.
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        The Service is provided "as is" without warranties of merchantability,
        fitness for a particular purpose, or non-infringement, to the maximum
        extent permitted by law. RentNova is a tool — it is not legal, tax,
        accounting, or real-estate advice. Decisions about leases, evictions,
        deposit handling, and so on are your responsibility.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our aggregate liability for any
        claim arising out of or relating to the Service is limited to the
        greater of (a) the fees you paid us in the 12 months before the event
        giving rise to the claim, or (b) EUR 100. We are not liable for
        indirect, incidental, special, consequential, or punitive damages, or
        for loss of profits, revenue, data, or goodwill.
      </p>
      <p>
        Nothing in these Terms limits liability for fraud, gross negligence,
        willful misconduct, or any other liability that cannot be limited under
        applicable law (including statutory consumer protection rights).
      </p>

      <h2>10. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from any claim arising from
        Your Content, your breach of these Terms, or your violation of any
        third-party right, including data-protection rights of your tenants.
      </p>

      <h2>11. Termination</h2>
      <p>
        You may terminate your account at any time from Settings. We may
        terminate or suspend immediately for material breach. On termination,
        access to the Service ends. Data is deleted as described in our
        <a href="/privacy"> Privacy Policy</a>.
      </p>

      <h2>12. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the <P>[Slovak Republic / your jurisdiction]</P>,
        without regard to conflict-of-laws rules. Disputes will be resolved
        before the competent courts of <P>[CITY, COUNTRY]</P>, unless mandatory
        consumer-protection law gives you a different right.
      </p>
      <p>
        If you are a consumer in the EU, you may also use the European
        Commission's Online Dispute Resolution platform at{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
          ec.europa.eu/consumers/odr
        </a>.
      </p>

      <h2>13. Changes to these Terms</h2>
      <p>
        We may amend these Terms. Material changes will be notified by email at
        least 14 days before they take effect. Continued use of the Service
        after the effective date constitutes acceptance.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions: <a href="mailto:contact@rentnova.app">contact@rentnova.app</a>.
      </p>
    </LegalLayout>
  );
}
